import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatCLP, formatFecha, hoyISO, nombreMes } from '../lib/format';
import { q, crearMovimiento, borrarMovimientoSimple } from '../lib/vinculos';
import { agruparPorSemana, agruparPorMesEnvios, inicioSemana, totalRetiro } from '../lib/envios';
import { useAccion } from '../lib/useAccion';
import ErrorBanner from './ErrorBanner';

const EMPRESA = 'Multservicexprezz';

const TABS = [
  { key: 'dia', label: '📦 Anotar día' },
  { key: 'semanas', label: '🗓️ Semanas' },
  { key: 'meses', label: '📊 Por mes' },
  { key: 'tarifas', label: '💲 Tarifas' },
];

const num = (v) => parseFloat(v) || 0;
const fechaCorta = (f) => formatFecha(f).slice(0, 5);
const plural = (n, s, p) => `${n} ${n === 1 ? s : p}`;

function Envios({ userId }) {
  const [tarifas, setTarifas] = useState([]);
  const [retiros, setRetiros] = useState([]);
  const [semanas, setSemanas] = useState([]);
  const [bolsillos, setBolsillos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('dia');
  const { saving, error, setError, run: ejecutar } = useAccion();

  // Anotar día
  const [fechaDia, setFechaDia] = useState(hoyISO());
  const [cantidades, setCantidades] = useState({}); // tarifa_id → cantidad
  const [notaDia, setNotaDia] = useState('');

  // Tarifas
  const [formTarifa, setFormTarifa] = useState({ zona: '', precio: '' });
  const [editTarifa, setEditTarifa] = useState(null); // { id, zona, precio }

  // Semanas
  const [editCobro, setEditCobro] = useState(null); // { inicio, paquetes, monto }
  const [pago, setPago] = useState(null); // { inicio, monto, bolsillo_id, fecha }

  const loadData = useCallback(async () => {
    if (!userId) return;
    try {
      const [t, r, s, b] = await Promise.all([
        q(supabase.from('envio_tarifas').select('*').eq('user_id', userId).order('zona')),
        q(supabase.from('envio_retiros').select('*').eq('user_id', userId).order('fecha', { ascending: false })),
        q(supabase.from('envio_semanas').select('*').eq('user_id', userId)),
        q(supabase.from('bolsillos').select('id, nombre').eq('user_id', userId).order('created_at')),
      ]);
      setTarifas(t || []);
      setRetiros(r || []);
      setSemanas(s || []);
      setBolsillos(b || []);
    } catch (e) {
      setError('Error cargando datos: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [userId, setError]);

  useEffect(() => { loadData(); }, [loadData]);

  const run = (accion) => ejecutar(accion, loadData);

  const tarifasActivas = tarifas.filter(t => t.activa);
  const retirosDelDia = retiros.filter(r => r.fecha === fechaDia);
  const semanasAgrupadas = agruparPorSemana(retiros, semanas);
  const meses = agruparPorMesEnvios(retiros, semanas);
  const totalNuevo = tarifasActivas.reduce((s, t) => s + (cantidades[t.id] || 0) * num(t.precio), 0);
  const paquetesNuevos = Object.values(cantidades).reduce((s, n) => s + (n || 0), 0);

  // Lo que debes: semanas cerradas o en curso que aún no pagas
  const pendientes = semanasAgrupadas.filter(s => !s.registro?.pagado && (s.paquetes > 0 || s.registro?.monto_cobrado));
  const totalPendiente = pendientes.reduce((s, x) => s + (x.registro?.monto_cobrado != null ? num(x.registro.monto_cobrado) : x.total), 0);

  // --- Anotar día ---
  const cambiarCantidad = (tarifaId, valor) => {
    const n = Math.max(0, Math.floor(Number(valor) || 0));
    setCantidades(c => ({ ...c, [tarifaId]: n }));
  };

  const handleGuardarDia = async (e) => {
    e.preventDefault();
    const filas = tarifasActivas
      .filter(t => (cantidades[t.id] || 0) > 0)
      .map(t => ({
        user_id: userId,
        fecha: fechaDia,
        tarifa_id: t.id,
        zona: t.zona,
        precio_unitario: num(t.precio),
        cantidad: cantidades[t.id],
        nota: notaDia || null,
      }));
    if (filas.length === 0) { setError('Anota al menos un paquete.'); return; }
    const ok = await run(() => q(supabase.from('envio_retiros').insert(filas)));
    if (ok) { setCantidades({}); setNotaDia(''); }
  };

  const handleBorrarRetiro = async (r) => {
    if (!window.confirm(`¿Borrar ${plural(r.cantidad, 'paquete', 'paquetes')} de ${r.zona} del ${formatFecha(r.fecha)}?`)) return;
    await run(() => q(supabase.from('envio_retiros').delete().eq('id', r.id)));
  };

  // --- Tarifas ---
  const handleAgregarTarifa = async (e) => {
    e.preventDefault();
    if (!formTarifa.zona.trim() || !formTarifa.precio) return;
    const ok = await run(() => q(supabase.from('envio_tarifas').insert({
      user_id: userId, zona: formTarifa.zona.trim(), precio: num(formTarifa.precio), activa: true,
    })));
    if (ok) setFormTarifa({ zona: '', precio: '' });
  };

  const handleGuardarTarifa = async (e) => {
    e.preventDefault();
    const ok = await run(() => q(supabase.from('envio_tarifas')
      .update({ zona: editTarifa.zona.trim(), precio: num(editTarifa.precio) }).eq('id', editTarifa.id)));
    if (ok) setEditTarifa(null);
  };

  const handleToggleTarifa = (t) => run(() => q(supabase.from('envio_tarifas').update({ activa: !t.activa }).eq('id', t.id)));

  // --- Semanas ---
  const guardarSemana = (inicio, cambios) => q(supabase.from('envio_semanas')
    .upsert({ user_id: userId, semana_inicio: inicio, ...cambios }, { onConflict: 'user_id,semana_inicio' }));

  const handleGuardarCobro = async (e) => {
    e.preventDefault();
    const ok = await run(() => guardarSemana(editCobro.inicio, {
      paquetes_cobrados: editCobro.paquetes === '' ? null : Math.floor(num(editCobro.paquetes)),
      monto_cobrado: editCobro.monto === '' ? null : num(editCobro.monto),
    }));
    if (ok) setEditCobro(null);
  };

  const abrirPago = (s) => setPago({
    inicio: s.inicio,
    monto: s.registro?.monto_cobrado != null ? s.registro.monto_cobrado : s.total,
    bolsillo_id: bolsillos[0]?.id || '',
    fecha: hoyISO(),
  });

  const handlePagar = async (e) => {
    e.preventDefault();
    const monto = num(pago.monto);
    if (monto <= 0) { setError('Ingresa el monto pagado.'); return; }
    const ok = await run(async () => {
      const mov = pago.bolsillo_id
        ? await crearMovimiento({
            userId,
            bolsilloId: pago.bolsillo_id,
            tipo: 'egreso',
            monto,
            descripcion: `${EMPRESA}: semana ${fechaCorta(pago.inicio)}`,
            fecha: pago.fecha,
            rubro: 'empresa',
          })
        : null;
      await guardarSemana(pago.inicio, {
        pagado: true,
        monto_pagado: monto,
        fecha_pago: pago.fecha,
        bolsillo_id: pago.bolsillo_id || null,
        movimiento_id: mov?.id || null,
      });
    });
    if (ok) setPago(null);
  };

  const handleDeshacerPago = async (s) => {
    const reg = s.registro;
    if (!window.confirm('¿Deshacer el pago de esta semana?' + (reg.movimiento_id ? '\n\nTambién se borrará la salida de dinero que se registró.' : ''))) return;
    await run(async () => {
      await guardarSemana(s.inicio, { pagado: false, monto_pagado: null, fecha_pago: null, bolsillo_id: null, movimiento_id: null });
      await borrarMovimientoSimple(reg.movimiento_id);
    });
  };

  if (loading) return (
    <div className="bolsillos-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
  );

  const semanaActual = inicioSemana(hoyISO());

  return (
    <div className="deudas-page">
      <div className="page-header">
        <div>
          <h2>Envíos Flex</h2>
          <p className="page-subtitle">Paquetes que retira {EMPRESA} y cuánto le debes</p>
        </div>
      </div>

      <ErrorBanner error={error} onClose={() => setError('')} />

      <div className="summary-cards" style={{ marginBottom: 16 }}>
        <div className="card">
          <div className="card-icon">💸</div>
          <div className="card-content">
            <h4>Por pagar</h4>
            <p className={`card-amount ${totalPendiente > 0 ? 'negative' : 'neutral'}`}>{formatCLP(totalPendiente)}</p>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{plural(pendientes.length, 'semana sin pagar', 'semanas sin pagar')}</span>
          </div>
        </div>
        {meses[0] && (
          <div className="card">
            <div className="card-icon">📦</div>
            <div className="card-content">
              <h4>{nombreMes(meses[0].clave)}</h4>
              <p className="card-amount neutral">{formatCLP(meses[0].total)}</p>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{plural(meses[0].paquetes, 'paquete', 'paquetes')} según tus registros</span>
            </div>
          </div>
        )}
      </div>

      <div className="negocio-tabs">
        {TABS.map(t => (
          <button key={t.key} className={`negocio-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {/* ─── ANOTAR DÍA ─── */}
      {tab === 'dia' && (
        tarifasActivas.length === 0 ? (
          <div className="card" style={{ display: 'block', textAlign: 'center', padding: 24 }}>
            <p style={{ fontWeight: 600, marginBottom: 8 }}>Primero crea tus tarifas</p>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>Anota cuánto te cobra {EMPRESA} por paquete en cada zona o comuna.</p>
            <button className="btn-primary" style={{ marginTop: 12 }} onClick={() => setTab('tarifas')}>Crear tarifas</button>
          </div>
        ) : (
          <>
            <form onSubmit={handleGuardarDia} className="card" style={{ display: 'block', padding: 16, marginBottom: 16 }}>
              <div className="form-group">
                <label>Día del retiro</label>
                <input type="date" value={fechaDia} onChange={e => setFechaDia(e.target.value)} />
              </div>
              {tarifasActivas.map(t => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{t.zona}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{formatCLP(t.precio)} c/u</div>
                  </div>
                  <button type="button" className="btn-secondary" style={{ padding: '6px 12px' }}
                    onClick={() => cambiarCantidad(t.id, (cantidades[t.id] || 0) - 1)}>−</button>
                  <input type="number" min="0" inputMode="numeric" value={cantidades[t.id] || ''} placeholder="0"
                    onChange={e => cambiarCantidad(t.id, e.target.value)}
                    style={{ width: 56, textAlign: 'center' }} />
                  <button type="button" className="btn-secondary" style={{ padding: '6px 12px' }}
                    onClick={() => cambiarCantidad(t.id, (cantidades[t.id] || 0) + 1)}>+</button>
                </div>
              ))}
              <div className="form-group" style={{ marginTop: 12 }}>
                <label>Nota (opcional)</label>
                <input type="text" value={notaDia} onChange={e => setNotaDia(e.target.value)} placeholder="ej: retiró Juan a las 15:00" />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <strong>{plural(paquetesNuevos, 'paquete', 'paquetes')} · {formatCLP(totalNuevo)}</strong>
                <button type="submit" className="btn-primary" disabled={saving || paquetesNuevos === 0}>{saving ? 'Guardando...' : 'Guardar'}</button>
              </div>
            </form>

            <h3 style={{ margin: '8px 0 12px', color: 'var(--text-secondary)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 1 }}>
              Anotado el {formatFecha(fechaDia)}
            </h3>
            {retirosDelDia.length === 0 ? (
              <p className="empty-text">Nada anotado este día.</p>
            ) : (
              <div className="card" style={{ display: 'block', padding: 12 }}>
                {retirosDelDia.map(r => (
                  <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', gap: 8 }}>
                    <span>{plural(r.cantidad, 'paquete', 'paquetes')} · {r.zona}{r.nota ? ` · ${r.nota}` : ''}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <strong>{formatCLP(totalRetiro(r))}</strong>
                      <button className="btn-icon-del small" title="Borrar" onClick={() => handleBorrarRetiro(r)}>×</button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </>
        )
      )}

      {/* ─── SEMANAS ─── */}
      {tab === 'semanas' && (
        semanasAgrupadas.length === 0 ? (
          <p className="empty-text">Aún no anotas paquetes.</p>
        ) : semanasAgrupadas.map(s => {
          const reg = s.registro;
          const difPaquetes = reg?.paquetes_cobrados != null ? reg.paquetes_cobrados - s.paquetes : null;
          const difMonto = reg?.monto_cobrado != null ? num(reg.monto_cobrado) - s.total : null;
          const editando = editCobro?.inicio === s.inicio;
          return (
            <div key={s.inicio} className="card" style={{ display: 'block', padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <strong>Semana {fechaCorta(s.inicio)} – {fechaCorta(s.fin)}{s.inicio === semanaActual ? ' (en curso)' : ''}</strong>
                {reg?.pagado
                  ? <span className="text-success" style={{ fontWeight: 600 }}>✅ Pagada {formatCLP(reg.monto_pagado)}</span>
                  : <span className="text-warning" style={{ fontWeight: 600 }}>Sin pagar</span>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, margin: '12px 0', textAlign: 'center', fontSize: '0.8rem' }}>
                {s.dias.map(d => (
                  <div key={d.fecha} style={{ padding: '6px 0', borderRadius: 8, background: d.paquetes ? 'var(--primary-xlight)' : 'transparent' }}>
                    <div style={{ color: 'var(--text-muted)' }}>{d.nombre}</div>
                    <div style={{ fontWeight: 700, fontSize: '1rem' }}>{d.paquetes || '–'}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span>Según tus registros: <strong>{plural(s.paquetes, 'paquete', 'paquetes')} · {formatCLP(s.total)}</strong></span>
              </div>
              {s.zonas.length > 1 && (
                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  {s.zonas.map(z => `${z.zona}: ${z.paquetes}`).join(' · ')}
                </div>
              )}

              {reg && (reg.paquetes_cobrados != null || reg.monto_cobrado != null) && !editando && (
                <div style={{ marginTop: 8 }}>
                  <span>{EMPRESA} cobra: <strong>
                    {reg.paquetes_cobrados != null ? plural(reg.paquetes_cobrados, 'paquete', 'paquetes') : ''}
                    {reg.paquetes_cobrados != null && reg.monto_cobrado != null ? ' · ' : ''}
                    {reg.monto_cobrado != null ? formatCLP(reg.monto_cobrado) : ''}
                  </strong></span>
                  {(difPaquetes > 0 || difMonto > 0) && (
                    <div className="alerta-rojo" style={{ marginTop: 8 }}>
                      <span className="alerta-rojo-icon">⚠️</span>
                      <div>
                        Te están cobrando de más:
                        {difPaquetes > 0 ? ` ${plural(difPaquetes, 'paquete', 'paquetes')}` : ''}
                        {difPaquetes > 0 && difMonto > 0 ? ',' : ''}
                        {difMonto > 0 ? ` ${formatCLP(difMonto)}` : ''}.
                        {' '}Revisa los días marcados arriba con lo que dice su planilla.
                      </div>
                    </div>
                  )}
                  {difPaquetes !== null && difPaquetes < 0 && (
                    <div className="field-hint">Te cobran {plural(-difPaquetes, 'paquete', 'paquetes')} menos de lo que anotaste.</div>
                  )}
                  {difPaquetes === 0 && (difMonto === null || difMonto === 0) && (
                    <div className="field-hint">✅ Cuadra con tus registros.</div>
                  )}
                </div>
              )}

              {editando ? (
                <form onSubmit={handleGuardarCobro} style={{ marginTop: 12 }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input type="number" min="0" inputMode="numeric" placeholder="Paquetes que cobran" style={{ flex: 1, minWidth: 120 }}
                      value={editCobro.paquetes} onChange={e => setEditCobro({ ...editCobro, paquetes: e.target.value })} />
                    <input type="number" min="0" inputMode="numeric" placeholder="Monto que cobran $" style={{ flex: 1, minWidth: 120 }}
                      value={editCobro.monto} onChange={e => setEditCobro({ ...editCobro, monto: e.target.value })} />
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={() => setEditCobro(null)}>Cancelar</button>
                    <button type="submit" className="btn-primary" disabled={saving}>Guardar</button>
                  </div>
                </form>
              ) : !reg?.pagado && (
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  <button className="btn-secondary" onClick={() => setEditCobro({
                    inicio: s.inicio,
                    paquetes: reg?.paquetes_cobrados ?? '',
                    monto: reg?.monto_cobrado ?? '',
                  })}>🧾 Anotar lo que cobran</button>
                  <button className="btn-primary" onClick={() => abrirPago(s)}>💸 Pagar semana</button>
                </div>
              )}
              {reg?.pagado && (
                <div style={{ marginTop: 8, fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                  Pagada el {formatFecha(reg.fecha_pago)}{' '}
                  <button onClick={() => handleDeshacerPago(s)} style={{ background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer', color: 'inherit', padding: 0 }}>Deshacer</button>
                </div>
              )}
            </div>
          );
        })
      )}

      {/* ─── POR MES ─── */}
      {tab === 'meses' && (
        meses.length === 0 ? (
          <p className="empty-text">Aún no anotas paquetes.</p>
        ) : meses.map(m => (
          <div key={m.clave} className="card" style={{ display: 'block', padding: 16, marginBottom: 12 }}>
            <strong>{nombreMes(m.clave)}</strong>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, gap: 8, flexWrap: 'wrap' }}>
              <span>Según tus registros: <strong>{plural(m.paquetes, 'paquete', 'paquetes')} · {formatCLP(m.total)}</strong></span>
              <span>Pagado: <strong className="text-success">{formatCLP(m.pagado)}</strong></span>
            </div>
            {m.paquetes > 0 && (
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>
                Promedio {formatCLP(m.total / m.paquetes)} por paquete
                {m.zonas.length > 1 ? ` · ${m.zonas.map(z => `${z.zona}: ${z.paquetes}`).join(' · ')}` : ''}
              </div>
            )}
          </div>
        ))
      )}

      {/* ─── TARIFAS ─── */}
      {tab === 'tarifas' && (
        <>
          <form onSubmit={handleAgregarTarifa} className="card" style={{ display: 'block', padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input type="text" placeholder="Zona o comuna (ej: Santiago Centro)" style={{ flex: 2, minWidth: 160 }}
                value={formTarifa.zona} onChange={e => setFormTarifa({ ...formTarifa, zona: e.target.value })} required />
              <input type="number" min="1" inputMode="numeric" placeholder="Precio $" style={{ flex: 1, minWidth: 100 }}
                value={formTarifa.precio} onChange={e => setFormTarifa({ ...formTarifa, precio: e.target.value })} required />
              <button type="submit" className="btn-primary" disabled={saving}>+ Agregar</button>
            </div>
            <span className="field-hint">Si cambias un precio, los paquetes ya anotados mantienen el precio que tenían ese día.</span>
          </form>
          {tarifas.length === 0 ? (
            <p className="empty-text">Aún no hay tarifas.</p>
          ) : (
            <div className="card" style={{ display: 'block', padding: 12 }}>
              {tarifas.map(t => editTarifa?.id === t.id ? (
                <form key={t.id} onSubmit={handleGuardarTarifa} style={{ display: 'flex', gap: 8, padding: '6px 0', flexWrap: 'wrap' }}>
                  <input type="text" style={{ flex: 2, minWidth: 140 }} value={editTarifa.zona}
                    onChange={e => setEditTarifa({ ...editTarifa, zona: e.target.value })} required />
                  <input type="number" min="1" style={{ flex: 1, minWidth: 90 }} value={editTarifa.precio}
                    onChange={e => setEditTarifa({ ...editTarifa, precio: e.target.value })} required />
                  <button type="button" className="btn-secondary" onClick={() => setEditTarifa(null)}>Cancelar</button>
                  <button type="submit" className="btn-primary" disabled={saving}>Guardar</button>
                </form>
              ) : (
                <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', gap: 8, opacity: t.activa ? 1 : 0.5 }}>
                  <span>{t.zona}{!t.activa ? ' (oculta)' : ''}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <strong>{formatCLP(t.precio)}</strong>
                    <button className="btn-icon-del small" title="Editar" style={{ color: 'var(--primary)', fontSize: '0.9rem' }}
                      onClick={() => setEditTarifa({ id: t.id, zona: t.zona, precio: t.precio })}>✎</button>
                    <button className="btn-icon-del small" title={t.activa ? 'Ocultar' : 'Mostrar'} onClick={() => handleToggleTarifa(t)}>
                      {t.activa ? '🙈' : '↺'}
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Modal: pagar semana */}
      {pago && (
        <div className="modal-overlay" onClick={() => setPago(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Pagar semana {fechaCorta(pago.inicio)}</h3>
              <button className="modal-close" onClick={() => setPago(null)}>×</button>
            </div>
            <form onSubmit={handlePagar}>
              <div className="form-group">
                <label>Monto pagado ($)</label>
                <input type="number" min="1" value={pago.monto} onChange={e => setPago({ ...pago, monto: e.target.value })} required autoFocus />
              </div>
              <div className="form-group">
                <label>¿De qué categoría sale?</label>
                <select value={pago.bolsillo_id} onChange={e => setPago({ ...pago, bolsillo_id: e.target.value })}>
                  <option value="">No registrar salida de dinero</option>
                  {bolsillos.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </select>
                {pago.bolsillo_id && <span className="field-hint">✅ Se registrará la salida de dinero automáticamente.</span>}
              </div>
              <div className="form-group">
                <label>Fecha de pago</label>
                <input type="date" value={pago.fecha} onChange={e => setPago({ ...pago, fecha: e.target.value })} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setPago(null)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Guardando...' : 'Pagar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Envios;
